// NexoNurse API utility — delegates to AppAPI when REACT_APP_MOCK !== 'true'.
// Mock mode (REACT_APP_MOCK=true) uses local mockData for offline development.
// All authentication is handled by apiFetch via the getToken callback.
//
// Token usage pattern:
//   import { useAuth } from '../contexts/AuthContext';
//   const { getAccessToken } = useAuth();
//   fetchDailyFocus(getAccessToken)
//
// Where AuthContext exposes: getAccessToken = () => accessToken (from state)
//
// Read functions use fetchOrMock(): try the real API, fall back to mock data
// on failure so dev.nexonurse.com shows demo content when the backend is down.
// Mutation functions (POST/PUT/DELETE) do NOT fall back — a failed write must
// surface as an error, not silently pretend to succeed.

import {
  mockSchedulingOverview,
  mockCapacityAnalysis,
  mockNoShowSummary,
  mockNoShowIntelligence,
  mockAccessLag,
  mockReferralAging,
  mockPhonePortal,
  mockPriorAuth,
  mockSignalLog,
  mockDirectorSchedulingAccess,
} from './mockSchedulingData';

import {
  mockUser,
  mockPriorityQueueData,
  mockQualityMetricsData,
  mockStaffingData,
  mockConsumerOverview,
  mockConsumerSurveys,
  mockConsumerReputation,
  mockConsumerComplaints,
  mockConsumerSignals,
  mockDirectorConsumerKPIs,
  mockDirectorFacilityComparison,
  mockDirectorComplaintAging,
  mockDirectorLegalEscalations,
  mockDirectorExperienceTrends,
  mockDirectorComplaintThemes,
  mockAccessMetricsData,
  mockStaffingAlertsData,
  mockWeeklyPrioritiesData,
  mockPerformanceDistributionData,
  mockExecutiveDashboardData,
  mockRoles,
  mockChecklists,
  mockChecklistSubmissions,
  mockOrgHierarchy,
  mockDailyFocusIssues,
  mockPerformanceBuckets,
  mockAssignments,
  mockInitiatives,
  mockDirectorDrilldownOptions,
  mockAccessMetrics,
  mockQualityMetrics,
  mockStaffingMetrics,
  mockFinanceMetrics,
  mockInitiativesMonitoring,
  mockInitiativesTrend,
  mockPerformanceBucketsByScope,
  mockPMTaskTemplates,
  mockPMTaskInstances,
  mockPMChecklists,
  mockPMStaffRoster,
  mockPMOverviewMetrics,
  mockPMCompletionHistory,
  mockDirectorPMMetrics,
} from './mockData';

import { apiFetch } from '../services/apiClient';

const USE_MOCK = process.env.REACT_APP_MOCK === 'true';
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const MOCK_DELAY = 300;

/**
 * Wrapper: try apiFetch, fall back to mock data on failure.
 * When the backend is unavailable (dev env, API down), the app
 * gracefully degrades to mock data instead of showing empty/broken UI.
 */
async function fetchOrMock(url, options, getToken, mockFn) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return mockFn(); }
  try {
    return await apiFetch(url, options, getToken);
  } catch (err) {
    console.warn(`[api] ${url} failed, falling back to mock data:`, err.message);
    return mockFn();
  }
}

// In-memory mutable list for initiatives when running in mock mode
let mockInitiativesList = [...mockInitiatives];

// ─── USER ─────────────────────────────────────────────────────────────────────
export async function fetchUser(getToken) {
  return fetchOrMock('/api/metrics/user', {}, getToken, () => mockUser);
}

// ─── PRIORITY QUEUE ───────────────────────────────────────────────────────────
export async function fetchPriorityQueue(_filters = {}, getToken) {
  return fetchOrMock('/api/queue', {}, getToken, () => (
    mockPriorityQueueData.map(item => {
      if (item.type === 'checklist' && item.checklistId) {
        const cl = mockChecklists.find(c => c.id === item.checklistId);
        return { ...item, questions: cl ? (cl.questions || []) : (item.questions || []) };
      }
      return { ...item };
    })
  ));
}

// ─── QUALITY METRICS ──────────────────────────────────────────────────────────
export async function fetchQualityMetrics(_filters = {}, getToken) {
  return fetchOrMock('/api/metrics/quality', {}, getToken, () => mockQualityMetricsData);
}

// ─── STAFFING ─────────────────────────────────────────────────────────────────
export async function fetchStaffing(_filters = {}, getToken) {
  return fetchOrMock('/api/metrics/staffing', {}, getToken, () => mockStaffingData);
}

// ─── CONSUMER INSIGHTS ────────────────────────────────────────────────────────
export async function fetchConsumerInsights(unitId = 'all', getToken) {
  return fetchOrMock(
    `/api/metrics/consumer?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockConsumerOverview[unitId] || mockConsumerOverview.all,
  );
}
export async function fetchConsumerSurveys(unitId = 'all', getToken) {
  return fetchOrMock(
    `/api/metrics/consumer/surveys?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockConsumerSurveys[unitId] || mockConsumerSurveys.all,
  );
}
export async function fetchConsumerReputation(unitId = 'all', getToken) {
  return fetchOrMock(
    `/api/metrics/consumer/reputation?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockConsumerReputation[unitId] || mockConsumerReputation.all,
  );
}
export async function fetchConsumerComplaints(unitId = 'all', getToken) {
  return fetchOrMock(
    `/api/metrics/consumer/complaints?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockConsumerComplaints[unitId] || mockConsumerComplaints.all,
  );
}
export async function fetchConsumerSignals(unitId = 'all', getToken) {
  return fetchOrMock(
    `/api/metrics/consumer/signals?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockConsumerSignals[unitId] || mockConsumerSignals.all,
  );
}

// ─── ACCESS METRICS ───────────────────────────────────────────────────────────
export async function fetchAccessMetrics(_filters = {}, getToken) {
  return fetchOrMock('/api/metrics/access', {}, getToken, () => mockAccessMetricsData);
}

// ─── STAFFING ALERTS ──────────────────────────────────────────────────────────
export async function fetchStaffingAlerts(_filters = {}, getToken) {
  return fetchOrMock('/api/metrics/staffing-alerts', {}, getToken, () => mockStaffingAlertsData);
}

// ─── WEEKLY PRIORITIES ────────────────────────────────────────────────────────
export async function fetchWeeklyPriorities(_filters = {}, getToken) {
  return fetchOrMock('/api/metrics/weekly-priorities', {}, getToken, () => mockWeeklyPrioritiesData);
}

// ─── PERFORMANCE DISTRIBUTION ─────────────────────────────────────────────────
export async function fetchPerformanceDistribution(_filters = {}, getToken) {
  return fetchOrMock('/api/metrics/performance-distribution', {}, getToken, () => mockPerformanceDistributionData);
}

// ─── EXECUTIVE DASHBOARD ──────────────────────────────────────────────────────
export async function fetchExecutiveDashboard(_serviceLineId, getToken) {
  return fetchOrMock('/api/metrics/executive-dashboard', {}, getToken, () => mockExecutiveDashboardData);
}

// ─── ROLE MANAGEMENT ──────────────────────────────────────────────────────────
export async function fetchRoles(getToken) {
  return fetchOrMock('/api/roles', {}, getToken, () => mockRoles);
}

export async function saveRole(roleData, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true, roleId: `role-${Date.now()}` }; }
  return apiFetch('/api/roles', { method: 'POST', body: JSON.stringify(roleData) }, getToken);
}

export async function deleteRole(roleId, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true }; }
  return apiFetch(`/api/roles/${roleId}`, { method: 'DELETE' }, getToken);
}

// ─── CHECKLISTS ───────────────────────────────────────────────────────────────
export async function fetchChecklists(filters = {}, getToken) {
  const params = filters.isRecurring !== undefined ? `?isRecurring=${filters.isRecurring}` : '';
  return fetchOrMock(`/api/checklists${params}`, {}, getToken, () => {
    let checklists = [...mockChecklists];
    if (filters.isRecurring !== undefined) {
      checklists = checklists.filter(c => c.isRecurring === filters.isRecurring);
    }
    return checklists;
  });
}

export async function saveChecklist(checklistData, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true, checklistId: `cl-${Date.now()}` }; }
  return apiFetch('/api/checklists', { method: 'POST', body: JSON.stringify(checklistData) }, getToken);
}

export async function deleteChecklist(checklistId, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true }; }
  return apiFetch(`/api/checklists/${checklistId}`, { method: 'DELETE' }, getToken);
}

export async function getChecklistSubmissions(checklistId, getToken) {
  return fetchOrMock(`/api/checklists/${checklistId}/submissions`, {}, getToken, () => {
    if (Array.isArray(mockChecklistSubmissions)) {
      return mockChecklistSubmissions.filter(s => s.checklistId === checklistId);
    }
    return mockChecklistSubmissions[checklistId] || [];
  });
}

export async function submitChecklist(submission, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true, id: `sub-${Date.now()}` }; }
  return apiFetch(`/api/checklists/${submission.checklistId}/submit`, {
    method: 'POST', body: JSON.stringify(submission)
  }, getToken);
}

// ─── ORG HIERARCHY ────────────────────────────────────────────────────────────
export async function fetchOrgHierarchy(getToken) {
  return fetchOrMock('/api/org-hierarchy', {}, getToken, () => mockOrgHierarchy);
}

export async function saveOrgHierarchy(data, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true }; }
  return apiFetch('/api/org-hierarchy', { method: 'PUT', body: JSON.stringify(data) }, getToken);
}

// ─── DAILY FOCUS (signals inbox) ─────────────────────────────────────────────
export async function fetchDailyFocus(getToken) {
  return fetchOrMock('/api/signals', {}, getToken, () => mockDailyFocusIssues);
}

export async function addToPriorityQueue(item, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true, id: `queue-${Date.now()}` }; }
  return apiFetch('/api/queue', { method: 'POST', body: JSON.stringify(item) }, getToken);
}

// ─── PERFORMANCE BUCKETS ──────────────────────────────────────────────────────
export async function fetchPerformanceBuckets({ level, marketId, areaId } = {}, getToken) {
  const params = new URLSearchParams();
  if (level) params.set('level', level);
  if (marketId) params.set('marketId', marketId);
  if (areaId) params.set('areaId', areaId);
  const qs = params.toString() ? `?${params}` : '';
  return fetchOrMock(`/api/metrics/performance-buckets${qs}`, {}, getToken, () => mockPerformanceBuckets);
}

// ─── INITIATIVES ──────────────────────────────────────────────────────────────
export async function fetchInitiatives(getToken) {
  return fetchOrMock('/api/initiatives', {}, getToken, () => mockInitiativesList);
}

export async function createInitiative(data, getToken) {
  if (USE_MOCK) {
    await delay(MOCK_DELAY);
    const id = `init-${Date.now()}`;
    const initiative = {
      id, name: data.name, description: data.description || '',
      assignedUnit: data.assignedUnit, assignedManager: data.assignedManager,
      assignedTo: data.assignedTo, priority: data.priority || 'medium',
      status: 'pending', targetDate: data.targetDate || ''
    };
    mockInitiativesList.push(initiative);
    return { success: true, id };
  }
  return apiFetch('/api/initiatives', { method: 'POST', body: JSON.stringify(data) }, getToken);
}

// ─── ASSIGNMENTS ──────────────────────────────────────────────────────────────
function initiativeToAssignment(init) {
  return {
    id: init.id, title: init.name, description: init.description,
    assignedTo: init.assignedTo, assignedBy: 'director-001',
    assignedByName: 'Director', assignedByRole: 'director',
    dueDate: init.targetDate ? new Date(init.targetDate).toISOString() : null,
    priority: init.priority, status: init.status, type: 'initiative',
    targetDate: init.targetDate, assignedUnit: init.assignedUnit,
    assignedManager: init.assignedManager
  };
}

export async function fetchAssignments(userId, getToken) {
  return fetchOrMock(`/api/assignments?userId=${encodeURIComponent(userId)}`, {}, getToken, () => {
    const fromAssignments = mockAssignments.filter(a => a.assignedTo === userId);
    const fromInitiatives = mockInitiativesList.filter(i => i.assignedTo === userId).map(initiativeToAssignment);
    return [...fromInitiatives, ...fromAssignments];
  });
}

export async function createAssignment(data, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true, id: `assign-${Date.now()}` }; }
  return apiFetch('/api/assignments', { method: 'POST', body: JSON.stringify(data) }, getToken);
}

export async function updateAssignmentStatus(id, status, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true }; }
  return apiFetch(`/api/assignments/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }, getToken);
}

// ─── DIRECTOR 6-PILLAR DATA ───────────────────────────────────────────────────
export async function getDirectorDrilldownOptions(getToken) {
  return fetchOrMock('/api/metrics/director-drilldown-options', {}, getToken, () => mockDirectorDrilldownOptions);
}

export async function getAccessMetrics(level, scopeId, getToken) {
  return fetchOrMock(
    `/api/metrics/director/access?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => mockAccessMetrics[scopeId] || mockAccessMetrics['region-primary-care'],
  );
}

export async function getQualityMetrics(level, scopeId, getToken) {
  return fetchOrMock(
    `/api/metrics/director/quality?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => mockQualityMetrics[scopeId] || mockQualityMetrics['region-primary-care'],
  );
}

export async function getStaffingMetrics(level, scopeId, getToken) {
  return fetchOrMock(
    `/api/metrics/director/staffing?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => mockStaffingMetrics[scopeId] || mockStaffingMetrics['region-primary-care'],
  );
}

export async function getFinanceMetrics(level, scopeId, getToken) {
  return fetchOrMock(
    `/api/metrics/director/finance?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => mockFinanceMetrics[scopeId] || mockFinanceMetrics['region-primary-care'],
  );
}

export async function getInitiativesData(level, scopeId, getToken) {
  return fetchOrMock(
    `/api/metrics/director/initiatives?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => ({
      initiatives: mockInitiativesMonitoring[scopeId] || mockInitiativesMonitoring['region-primary-care'],
      trend: mockInitiativesTrend[scopeId] || mockInitiativesTrend['region-primary-care'],
    }),
  );
}

export async function getConsumerInsights(_level, _scopeId, getToken) {
  return fetchOrMock(
    `/api/metrics/director/consumer?scopeId=${encodeURIComponent(_scopeId)}`, {}, getToken,
    () => ({
      kpis: mockDirectorConsumerKPIs,
      facilities: mockDirectorFacilityComparison,
      aging: mockDirectorComplaintAging,
      legalEscalations: mockDirectorLegalEscalations,
      trends: mockDirectorExperienceTrends,
      themes: mockDirectorComplaintThemes,
    }),
  );
}

export async function getPerformanceBucketsByScope(scopeId, _pillarType, getToken) {
  return fetchOrMock(
    `/api/metrics/performance-buckets-by-scope?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => {
      const buckets = mockPerformanceBucketsByScope[scopeId] || [];
      return buckets.map((b) => ({
        ...b,
        value: `${b.score}%`,
        target: `${b.target}%`,
        trend: b.trend ? { direction: b.trend >= 0 ? 'up' : 'down', value: `${Math.abs(b.trend)}%` } : null,
      }));
    },
  );
}

// ─── FINANCIAL TRACKING (NX-005) ────────────────────────────────────────────
export async function fetchFinancialOverview(_period, getToken) {
  return fetchOrMock('/api/finance/overview', {}, getToken, () => ({ success: true }));
}

export async function fetchBudgetCards(_period, getToken) {
  return fetchOrMock('/api/finance/budget-cards', {}, getToken, () => ({ success: true }));
}

export async function submitCSVUpload(_file, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true, uploadId: `up-${Date.now()}`, status: 'queued' }; }
  return apiFetch('/api/uploads/csv', { method: 'POST' }, getToken);
}

export async function fetchUploadHistory(getToken) {
  return fetchOrMock('/api/uploads/history', {}, getToken, () => ({ success: true }));
}

export async function fetchFieldMappings(_cardType, getToken) {
  return fetchOrMock('/api/finance/field-mappings', {}, getToken, () => ({ success: true }));
}

export async function saveFieldMapping(cardType, mappings, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true }; }
  return apiFetch('/api/finance/field-mappings', { method: 'PUT', body: JSON.stringify({ cardType, mappings }) }, getToken);
}

export async function fetchFinanceSignals(getToken) {
  return fetchOrMock('/api/finance/signals', {}, getToken, () => ({ success: true }));
}

export async function getFinanceDrilldownOptions(getToken) {
  return fetchOrMock('/api/finance/drilldown-options', {}, getToken, () => ({ success: true }));
}

// ─── PRACTICE MANAGEMENT ────────────────────────────────────────────────────

export async function getPMTaskLibrary(unitId, getToken) {
  return fetchOrMock(
    `/api/practice-management/tasks?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockPMTaskTemplates,
  );
}

export async function getPMPriorityQueue(unitId, date, getToken) {
  return fetchOrMock(
    `/api/practice-management/queue?unitId=${encodeURIComponent(unitId)}&date=${encodeURIComponent(date)}`, {}, getToken,
    () => mockPMTaskInstances.filter(i => i.dueDate === date),
  );
}

export async function getPMChecklists(unitId, getToken) {
  return fetchOrMock(
    `/api/practice-management/checklists?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockPMChecklists,
  );
}

export async function getPMOverviewMetrics(unitId, getToken) {
  return fetchOrMock(
    `/api/practice-management/overview?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockPMOverviewMetrics[unitId] || mockPMOverviewMetrics['unit-main-st'],
  );
}

export async function getPMCompletionHistory(unitId, _filters, getToken) {
  return fetchOrMock(
    `/api/practice-management/history?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockPMCompletionHistory,
  );
}

export async function getPMStaffRoster(unitId, getToken) {
  return fetchOrMock(
    `/api/practice-management/staff?unitId=${encodeURIComponent(unitId)}`, {}, getToken,
    () => mockPMStaffRoster,
  );
}

export async function getDirectorPMMetrics(scopeType, scopeId, getToken) {
  return fetchOrMock(
    `/api/practice-management/director?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => mockDirectorPMMetrics[scopeId] || mockDirectorPMMetrics['service-line-primary-care'],
  );
}

export async function getDirectorPMOrphanedEvents(scopeType, scopeId, getToken) {
  return fetchOrMock(
    `/api/practice-management/director/orphaned?scopeType=${encodeURIComponent(scopeType)}&scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => {
      const data = mockDirectorPMMetrics[scopeId] || mockDirectorPMMetrics['service-line-primary-care'];
      return data?.orphanedEvents || [];
    },
  );
}

// ─── PRACTICE MANAGEMENT — MUTATIONS ──────────────────────────────────────

export async function updatePMTask(taskId, updates, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { ...updates, id: taskId }; }
  return apiFetch(`/api/practice-management/tasks/${encodeURIComponent(taskId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
  }, getToken);
}

export async function createPMTask(task, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { ...task, id: `task-custom-${Date.now()}`, isCustom: true, active: true }; }
  return apiFetch('/api/practice-management/tasks', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(task),
  }, getToken);
}

export async function restorePMTask(taskId, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: taskId }; }
  return apiFetch(`/api/practice-management/tasks/${encodeURIComponent(taskId)}/restore`, {
    method: 'POST',
  }, getToken);
}

export async function completePMTask(instanceId, { note, linkedOutputs } = {}, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: instanceId, status: 'completed' }; }
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/complete`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note, linkedOutputs }),
  }, getToken);
}

export async function skipPMTask(instanceId, reason, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: instanceId, status: 'skipped' }; }
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/skip`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  }, getToken);
}

export async function delegatePMTask(instanceId, delegatee, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: instanceId, status: 'delegated' }; }
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/delegate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ delegatee }),
  }, getToken);
}

export async function reassignPMOrphanedTask(instanceId, newOwner, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: instanceId, isOrphaned: false }; }
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/reassign`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ newOwner }),
  }, getToken);
}

export async function deferPMOrphanedTask(instanceId, deferDate, reason, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: instanceId, status: 'skipped' }; }
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/defer`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ deferDate, reason }),
  }, getToken);
}

export async function reorderPMQueue(instanceId, toIndex, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true }; }
  return apiFetch(`/api/practice-management/queue/${encodeURIComponent(instanceId)}/reorder`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ toIndex }),
  }, getToken);
}

export async function updatePMChecklistStep(checklistId, stepId, updates, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: checklistId }; }
  return apiFetch(`/api/practice-management/checklists/${encodeURIComponent(checklistId)}/steps/${encodeURIComponent(stepId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
  }, getToken);
}

// ─── PRACTICE MANAGEMENT — WORKFLOWS (DAG) ───────────────────────────────

export async function getPMWorkflows(getToken) {
  return fetchOrMock('/api/practice-management/workflows', {}, getToken, () => []);
}

export async function getPMWorkflow(workflowId, getToken) {
  return fetchOrMock(
    `/api/practice-management/workflows/${encodeURIComponent(workflowId)}`, {}, getToken,
    () => null,
  );
}

export async function createPMWorkflowInstance(workflowId, { activatedAt, dueDate } = {}, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: `wfi-mock-${Date.now()}` }; }
  return apiFetch(`/api/practice-management/workflows/${encodeURIComponent(workflowId)}/instances`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ activatedAt, dueDate }),
  }, getToken);
}

export async function getPMWorkflowInstances(workflowId, getToken) {
  return fetchOrMock(
    `/api/practice-management/workflows/${encodeURIComponent(workflowId)}/instances`, {}, getToken,
    () => [],
  );
}

export async function getPMWorkflowInstanceState(instanceId, getToken) {
  return fetchOrMock(
    `/api/practice-management/workflows/instances/${encodeURIComponent(instanceId)}`, {}, getToken,
    () => null,
  );
}

export async function selectPMWorkflowGate(instanceId, nodeId, selection, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true }; }
  return apiFetch(`/api/practice-management/workflows/instances/${encodeURIComponent(instanceId)}/gate/${encodeURIComponent(nodeId)}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selection }),
  }, getToken);
}

export async function validatePMWorkflowEdge(workflowId, from, to, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { valid: true }; }
  return apiFetch('/api/practice-management/workflows/validate-edge', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workflowId, from, to }),
  }, getToken);
}

export async function createPMWorkflow(workflow, getToken) {
  if (USE_MOCK) {
    await delay(MOCK_DELAY);
    return { ...workflow, id: `wf-custom-${Date.now()}` };
  }
  return apiFetch('/api/practice-management/workflows', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(workflow),
  }, getToken);
}

export async function updatePMWorkflow(workflowId, updates, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { id: workflowId, ...updates }; }
  return apiFetch(`/api/practice-management/workflows/${encodeURIComponent(workflowId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
  }, getToken);
}

export async function deletePMWorkflow(workflowId, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true }; }
  return apiFetch(`/api/practice-management/workflows/${encodeURIComponent(workflowId)}`, {
    method: 'DELETE',
  }, getToken);
}

// ─── SCHEDULING & ACCESS (NX-007) ───────────────────────────────────────────

// Manager L1
export async function fetchSchedulingOverview(_unitId, getToken) {
  return fetchOrMock('/api/scheduling/overview', {}, getToken, () => mockSchedulingOverview);
}

// Manager L2 — Capacity (FSA.2)
export async function fetchCapacityAnalysis(_unitId, period = 'week', getToken) {
  return fetchOrMock(
    `/api/scheduling/capacity?period=${encodeURIComponent(period)}`, {}, getToken,
    () => mockCapacityAnalysis,
  );
}

// Manager L2 — No-Show (FSA.3)
export async function fetchNoShowSummary(_unitId, getToken) {
  return fetchOrMock('/api/scheduling/noshow/summary', {}, getToken, () => mockNoShowSummary);
}

export async function fetchNoShowIntelligence(_unitId, _filters = {}, getToken) {
  return fetchOrMock('/api/scheduling/noshow/intelligence', {}, getToken, () => mockNoShowIntelligence);
}

// Manager L2 — Access Lag (FSA.4)
export async function fetchAccessLag(_unitId, getToken) {
  return fetchOrMock('/api/scheduling/access-lag', {}, getToken, () => mockAccessLag);
}

// Manager L2 — Referral Aging (FSA.5)
export async function fetchReferralAging(_unitId, getToken) {
  return fetchOrMock('/api/scheduling/referrals', {}, getToken, () => mockReferralAging);
}

export async function assignReferral(referralId, staffId, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true, referralId, assignedTo: staffId }; }
  return apiFetch(`/api/scheduling/referrals/${encodeURIComponent(referralId)}/assign`, { method: 'POST', body: JSON.stringify({ staffId }) }, getToken);
}

// Manager L2 — Phone & Portal (FSA.6)
export async function fetchPhonePortal(_unitId, getToken) {
  return fetchOrMock('/api/scheduling/phone-portal', {}, getToken, () => mockPhonePortal);
}

export async function submitManualPhoneStats(_unitId, data, getToken) {
  if (USE_MOCK) { await delay(MOCK_DELAY); return { success: true, received: data }; }
  return apiFetch('/api/scheduling/phone-portal/manual', { method: 'POST', body: JSON.stringify(data) }, getToken);
}

// Manager L2 — Prior Auth (FSA.7)
export async function fetchPriorAuth(_unitId, getToken) {
  return fetchOrMock('/api/scheduling/prior-auth', {}, getToken, () => mockPriorAuth);
}

// Signal Log (FSA.8)
export async function fetchSignalLog(_unitId, getToken) {
  return fetchOrMock('/api/scheduling/signals/log', {}, getToken, () => mockSignalLog);
}

// Director — Scheduling
export async function getDirectorSchedulingOverview(scopeId, getToken) {
  return fetchOrMock(
    `/api/scheduling/director/overview?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => (mockDirectorSchedulingAccess[scopeId] || mockDirectorSchedulingAccess['region-primary-care']).overview,
  );
}

export async function getDirectorSchedulingCapacity(scopeId, getToken) {
  return fetchOrMock(
    `/api/scheduling/director/capacity?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => (mockDirectorSchedulingAccess[scopeId] || mockDirectorSchedulingAccess['region-primary-care']).capacity,
  );
}

export async function getDirectorSchedulingNoShow(scopeId, getToken) {
  return fetchOrMock(
    `/api/scheduling/director/noShow?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => (mockDirectorSchedulingAccess[scopeId] || mockDirectorSchedulingAccess['region-primary-care']).noShow,
  );
}

export async function getDirectorSchedulingAccessLag(scopeId, getToken) {
  return fetchOrMock(
    `/api/scheduling/director/accessLag?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => (mockDirectorSchedulingAccess[scopeId] || mockDirectorSchedulingAccess['region-primary-care']).accessLag,
  );
}

export async function getDirectorSchedulingPhonePortal(scopeId, getToken) {
  return fetchOrMock(
    `/api/scheduling/director/phonePortal?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => (mockDirectorSchedulingAccess[scopeId] || mockDirectorSchedulingAccess['region-primary-care']).phonePortal,
  );
}

export async function getDirectorSchedulingPriorAuth(scopeId, getToken) {
  return fetchOrMock(
    `/api/scheduling/director/priorAuth?scopeId=${encodeURIComponent(scopeId)}`, {}, getToken,
    () => (mockDirectorSchedulingAccess[scopeId] || mockDirectorSchedulingAccess['region-primary-care']).priorAuth,
  );
}
